import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Grid from 'components/core/Grid';
import Typography from 'components/core/Typography';
import Autocomplete from 'components/core/Autocomplete';
import Input from 'components/core/Input';
import Button from 'components/core/Button';
import LookupTableInput from 'components/LookupTableInput';

import evidenceActions from 'actions/evidenceActions';
import observationActions from 'actions/observationActions';
import personActions from 'actions/personActions';
import lookupTableActions from 'actions/lookupTableActions';
import eventActions from 'actions/eventActions';
import { Evidence } from 'domain';
import CommonUtils from 'utils/CommonUtils';

const initialState = {
    name: '',
    evidenceFormat: null,
    sourceOwned: null,
    subjects: [],
    observations: [],
    voucher: {
        repository: null,
        catalogNumber: '',
        additionalNumber: '',
        voucherSource: null,
        event: null
    },
    literature: {
        title: '',
        doi: '',
        publisher: '',
        year: '',
        volume: '',
        issue: '',
        startPage: '',
        endPage: '',
        authors: [],
        event: null
    },
    file: {
        name: '',
        url: '',
        event: null
    }
};

const EvidenceForm = ({ observationId, onSubmit, evidences, initialEvidence }) => {
    console.log('EvidenceForm props - observationId:', observationId);
    const dispatch = useDispatch();
    const [evidence, setEvidence] = useState(initialEvidence || initialState);
    const [errors, setErrors] = useState({});
    const options = useSelector(({ lookupTable }) => lookupTable.options);
    const personList = useSelector(({ person }) => person.list);
    const events = useSelector(({ event }) => event.list);
    const observations = useSelector(({ observation }) => observation.list);
    const currentUser = useSelector(({ auth }) => auth.me);

    useEffect(() => {
        dispatch(lookupTableActions.fetchAllOptions());
        dispatch(personActions.list());
        dispatch(eventActions.list());
        dispatch(observationActions.list());
    }, [dispatch]);

    useEffect(() => {
        if (observationId && observations.length > 0) {
            const targetObservation = observations.find(obs => obs.id === observationId);
            if (targetObservation && !evidence.observations?.find(obs => obs.id === observationId)) {
                setEvidence(prev => ({
                    ...prev,
                    observations: [targetObservation]
                }));
            }
        }
    }, [observationId, observations, evidence.observations]);

    useEffect(() => {
        if (initialEvidence) {
            setEvidence(initialEvidence);
        }
    }, [initialEvidence]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});
        
        const evidenceNames = evidences.filter(e => e.id !== initialEvidence?.id).map(e => e.name);
        if (!evidence.name) {
            setErrors({ name: 'Evidence name is required' });
            return;
        }
        if (evidenceNames.includes(evidence.name)) {
            setErrors({ name: 'Evidence with this name already exists' });
            return;
        }

        try {
            // Create Evidence object like ObservationEdit does
            const evidenceToSave = new Evidence();
            
            if (initialEvidence?.id) {
                evidenceToSave.id = initialEvidence.id;
            }
            
            evidenceToSave.set('label', evidence.name);
            evidenceToSave.set('evidenceFormat', CommonUtils.createPointer('EvidenceFormat', evidence.evidenceFormat?.id));
            evidenceToSave.set('sourceOwned', CommonUtils.createPointer('SourceOwned', evidence.sourceOwned?.id));
            evidenceToSave.set('createdBy', CommonUtils.createPointer('_User', currentUser.id));
            
            // Set the composite data for related objects
            evidenceToSave.set('subjects', evidence.subjects?.map(s => s.id) || []);
            evidenceToSave.set('voucher', evidence.voucher);
            evidenceToSave.set('literature', evidence.literature);
            evidenceToSave.set('file', evidence.file);
            evidenceToSave.set('observations', evidence.observations?.map(o => o.id) || []);

            const result = await dispatch(evidenceActions.save(evidenceToSave));
            
            onSubmit(result.value);
        } catch (err) {
            setErrors(err.inner?.reduce((acc, curr) => {
                acc[curr.path] = curr.message;
                return acc;
            }, {}) || {});
        }
    };

    const handleEvidenceFormatChange = (value) => {
        setEvidence(prev => ({
            ...prev,
            evidenceFormat: value,
            voucher: initialState.voucher,
            literature: initialState.literature,
            file: initialState.file
        }));
    };

    const handleFieldChange = (field, value) => {
        setEvidence(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const renderVoucherFields = () => (
        <>
            <Grid item xs={12} md={6}>
                <LookupTableInput
                    label="Repository"
                    value={evidence.voucher.repository}
                    onChange={(value) => handleFieldChange('voucher', { ...evidence.voucher, repository: value })}
                    options={options.repositories}
                    getOptionLabel={(option) => option?.get('label')}
                    error={errors['voucher.repository']}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <Input
                    label="Catalog Number"
                    value={evidence.voucher.catalogNumber}
                    onChange={(value) => handleFieldChange('voucher', { ...evidence.voucher, catalogNumber: value })}
                    error={errors['voucher.catalogNumber']}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <LookupTableInput
                    label="Voucher Source"
                    value={evidence.voucher.voucherSource}
                    onChange={(value) => handleFieldChange('voucher', { ...evidence.voucher, voucherSource: value })}
                    options={personList}
                    error={errors['voucher.voucherSource']}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <Input
                    label="Additional Number"
                    value={evidence.voucher.additionalNumber}
                    onChange={(value) => handleFieldChange('voucher', { ...evidence.voucher, additionalNumber: value })}
                    error={errors['voucher.additionalNumber']}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <Autocomplete
                    label="Event"
                    value={evidence.voucher.event}
                    onChange={(value) => handleFieldChange('voucher', { ...evidence.voucher, event: value })}
                    options={events}
                    getOptionLabel={(option) => option?.get('name') || ''}
                    error={errors['voucher.event']}
                />
            </Grid>
        </>
    );

    const renderLiteratureFields = () => (
        <>
            <Grid item xs={12}>
                <Input
                    label="Title"
                    value={evidence.literature.title}
                    onChange={(value) => handleFieldChange('literature', { ...evidence.literature, title: value })}
                    error={errors['literature.title']}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <Input
                    label="DOI"
                    value={evidence.literature.doi}
                    onChange={(value) => handleFieldChange('literature', { ...evidence.literature, doi: value })}
                    error={errors['literature.doi']}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <Input
                    label="Publisher"
                    value={evidence.literature.publisher}
                    onChange={(value) => handleFieldChange('literature', { ...evidence.literature, publisher: value })}
                    error={errors['literature.publisher']}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <Input
                    label="Year"
                    value={evidence.literature.year}
                    onChange={(value) => handleFieldChange('literature', { ...evidence.literature, year: value })}
                    error={errors['literature.year']}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <Input
                    label="Volume"
                    value={evidence.literature.volume}
                    onChange={(value) => handleFieldChange('literature', { ...evidence.literature, volume: value })}
                    error={errors['literature.volume']}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <Input
                    label="Issue"
                    value={evidence.literature.issue}
                    onChange={(value) => handleFieldChange('literature', { ...evidence.literature, issue: value })}
                    error={errors['literature.issue']}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <Input
                    label="Start Page"
                    value={evidence.literature.startPage}
                    onChange={(value) => handleFieldChange('literature', { ...evidence.literature, startPage: value })}
                    error={errors['literature.startPage']}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <Input
                    label="End Page"
                    value={evidence.literature.endPage}
                    onChange={(value) => handleFieldChange('literature', { ...evidence.literature, endPage: value })}
                    error={errors['literature.endPage']}
                />
            </Grid>
            <Grid item xs={12}>
                <LookupTableInput
                    label="Authors"
                    multiple
                    value={evidence.literature.authors}
                    onChange={(value) => handleFieldChange('literature', { ...evidence.literature, authors: value })}
                    options={personList}
                    getOptionLabel={(option) => option?.get('firstName') || ''}
                    error={errors['literature.authors']}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <Autocomplete
                    label="Event"
                    value={evidence.literature.event}
                    onChange={(value) => handleFieldChange('literature', { ...evidence.literature, event: value })}
                    options={events}
                    getOptionLabel={(option) => option?.get('name') || ''}
                    error={errors['literature.event']}
                />
            </Grid>
        </>
    );

    const renderFileFields = () => (
        <>
            <Grid item xs={12} md={6}>
                <Input
                    label="File Name"
                    value={evidence.file.name}
                    onChange={(value) => handleFieldChange('file', { ...evidence.file, name: value })}
                    error={errors['file.name']}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <Input
                    label="URL"
                    value={evidence.file.url}
                    onChange={(value) => handleFieldChange('file', { ...evidence.file, url: value })}
                    error={errors['file.url']}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <Autocomplete
                    label="Event"
                    value={evidence.file.event}
                    onChange={(value) => handleFieldChange('file', { ...evidence.file, event: value })}
                    options={events}
                    getOptionLabel={(option) => option?.get('name') || ''}
                    error={errors['file.event']}
                />
            </Grid>
        </>
    );

    const renderTypeSpecificFields = () => {
        const formatType = evidence.evidenceFormat?.get('filteredFor');
        switch (formatType) {
            case 'Voucher':
                return renderVoucherFields();
            case 'Literature':
                return renderLiteratureFields();
            case 'File':
                return renderFileFields();
            default:
                return null;
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <Grid container spacing={4} sx={{ margin: 4 }}>
                <Grid item xs={12}>
                    <Typography variant="h4">Add Evidence</Typography>
                </Grid>

                <Grid item xs={12}>
                    <Input
                        label="Evidence Name"
                        value={evidence.name}
                        onChange={(value) => handleFieldChange('name', value)}
                        error={errors.name}
                        required
                    />
                </Grid>

                <Grid item xs={12}>
                    <LookupTableInput
                        id="subjects"
                        label="Evidence Subjects"
                        value={evidence.subjects}
                        onChange={(value) => handleFieldChange('subjects', value)}
                        options={options.subjects}
                        getOptionLabel={(option) => `${option?.get('label')} (${option?.get('shortCode')})`}
                        error={errors.subjects}
                        multiple
                    />
                </Grid>

                <Grid item xs={12}>
                    <LookupTableInput
                        id="evidenceFormat"
                        label="Evidence Format"
                        value={evidence.evidenceFormat}
                        onChange={handleEvidenceFormatChange}
                        options={options.evidenceFormats}
                        getOptionLabel={(option) => `${option?.get('label')} - ${option?.get('filteredFor')}`}
                        error={errors.evidenceFormat}
                    />
                </Grid>

                <Grid item xs={12}>
                    <LookupTableInput
                        id="sourceOwned"
                        label="Source Owned"
                        value={evidence.sourceOwned}
                        onChange={(value) => handleFieldChange('sourceOwned', value)}
                        options={options.sourceOwneds}
                        getOptionLabel={(option) => option?.get('label')}
                        error={errors.sourceOwned}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Autocomplete
                        label="Linked Observations"
                        value={evidence.observations}
                        onChange={(value) => handleFieldChange('observations', value)}
                        options={observations}
                        getOptionLabel={(option) => {
                            const obsLabel = option?.get('label') || 'Unlabeled Observation';
                            const name = option?.get('name');
                            if (name && name.get('taxonomy')) {
                                const primaryName = name.get('taxonomy').get('primaryName');
                                return `${obsLabel} - ${primaryName}`;
                            }
                            return obsLabel;
                        }}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        multiple
                        error={errors.observations}
                    />
                </Grid>

                {renderTypeSpecificFields()}

                <Grid item xs={12}>
                    <Button type="submit">{initialEvidence?.id ? 'Update' : 'Save'} Evidence</Button>
                </Grid>
            </Grid>
        </form>
    );
};

export default EvidenceForm;